import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { BsChatService } from '@brightside-web/desktop/data-access/core-services';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HardshipRequiredDocsService } from '../hardship-required-docs.service';

export interface HardshipDetails {
  eventName: string;
  titleKey: string;
  descriptionKey?: string;
  ctaKey: string;
  ctaAction: Function;
  secondaryCtaKey?: string;
  secondaryAction?: Function;
  statusKey?: string;
}

export type HardshipDetailsViews = {
  hardship_confirm_request: HardshipDetails;
  hardship_request_details: HardshipDetails;
  hardship_submit_application: HardshipDetails;
};

export type HardshipActiveView = keyof HardshipDetailsViews;

@Component({
    selector: 'bw-hardship-request-details',
    imports: [CommonModule, TranslateModule, SharedDesktopModule, MicroUiMoleculeModule, BrightsideUiModule, ReactiveFormsModule],
    templateUrl: './hardship-request-details.component.html',
    styleUrl: './hardship-request-details.component.scss'
})
export class HardshipRequestDetailsComponent {

  hardshipService = inject(HardshipRequiredDocsService);
  translate = inject(TranslateService);

  legalAcknowledged = signal<boolean>(false);
  bsChatSvc = inject(BsChatService);
  private readonly _router = inject(Router);

  confirmDataForm: FormGroup = new FormGroup({
    confirmCheckbox: new FormControl('', [Validators.requiredTrue]),
  });

  hardshipDetailsViews: HardshipDetailsViews = {
    hardship_confirm_request: {
      eventName: 'HARDSHIP_CONFIRM_REQUEST',
      titleKey: 'HARDSHIP_CONFIRM_REQUEST_TITLE',
      descriptionKey: 'HARDSHIP_CONFIRM_REQUEST_DESC',
      ctaKey: 'CONFIRM',
      statusKey: 'HARDSHIP_CONFIRM_REQUEST_PENDING_DOC',
      secondaryCtaKey: 'MESSAGE_FA',
      ctaAction: () => this.confirm(),
      secondaryAction: () => this.bsChatSvc.showChat(),
    },
    hardship_request_details: {
      eventName: 'HARDSHIP_REQUEST_DETAILS',
      titleKey: 'HARDSHIP_REQUEST_DETAILS_TITLE',
      ctaKey: 'MESSAGE_FA',
      statusKey: 'HARDSHIP_REQUEST_DETAILS_PENDING_REVIEW',
      ctaAction: () => this.bsChatSvc.showChat(),
    },
    hardship_submit_application: {
      eventName: 'HARDSHIP_SUBMIT_APPLICATION',
      titleKey: 'HARDSHIP_SUBMIT_APPLICATION_TITLE',
      descriptionKey: 'HARDSHIP_SUBMIT_APPLICATION_DESC',
      ctaKey: 'HARDSHIP_SUBMIT_APPLICATION_CTA',
      ctaAction: () => this.submitApplication(),
    },
  };

  readonly activeView = this.hardshipService.activeView;
  readonly processing = this.hardshipService.processing;

  readonly submissionLabelRight = computed(() => {
    const titles = this.hardshipService.uploadedDocumentTitles();
    if (!titles || titles.length === 0) {
      return '';
    }
    const translated = titles.map(t => this.translate.instant(t));
    return translated.join(', ');
  });

  constructor() {
    this.hardshipService.getHardshipDocuments();
  }

  confirm() {
    this._router.navigate(['hardship','documents']);
  }

  submitApplication() {
    this.hardshipService.submitApplication();
  }

  acknowledge() {
    this.legalAcknowledged.set(!this.legalAcknowledged());
  }
}

